import { BlobReadSync } from "@pdfking/blob"
import type { BlobArrayType, BlobCreateType, BlobInput, BlobPartType } from "@pdfking/blob"
import { Max, Min } from "@pdfking/math"
import type { Bytes } from "@pdfking/structs"
import { BytesQ, StringQ, ContentType } from "@pdfking/structs"
import { StringToUInt8, ToArray, UInt8Merge, WaitPromises } from "@pdfking/tfunc"

type BlobState = Pick<BlobArrayType, "offsets" | "parts" | "size">

// Parts are held exactly as they came — bytes stay the view they were, a
// platform blob stays the file it may be — and every question below is
// answered by walking them. Nothing here wraps a part in anything.

const partSize = (part: BlobPartType): number => (BytesQ(part) ? part.byteLength : part.size)

// A view, never a read: subarray for bytes, the platform's own slice for its
// blob — which is a range over the file, not a trip to it — and the sync
// blob's slice, which promises the same.
const partView = (part: BlobPartType, start: number, end: number): BlobPartType =>
  BytesQ(part) ? part.subarray(start, end) : part.slice(start, end)

// The one moment a part becomes bytes. Sync has two doors: bytes already are,
// and a platform blob goes through BlobReadSync — which is the browser's to
// answer and node's to refuse.
const partBytes = async (part: BlobPartType): Promise<Uint8Array> =>
  BytesQ(part) ? part : new Uint8Array(await part.arrayBuffer())

const partBytesSync = (part: BlobPartType): Uint8Array =>
  BytesQ(part)
    ? part
    : new Uint8Array(part instanceof Blob ? BlobReadSync(part) : part.arrayBufferSync())

// Binary search to find the part index containing position
const findPartIndex = ({ offsets, parts }: BlobState, pos: number): number => {
  let lo = 0
  let hi = parts.length
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (offsets[mid] + partSize(parts[mid]) <= pos) lo = mid + 1
    else hi = mid
  }
  return lo
}

const clear = (state: BlobState) => {
  state.parts.length = 0
  state.offsets.length = 0
  state.size = 0
}

const add = (state: BlobState, part: BlobPartType) => {
  state.parts.push(part)
  state.offsets.push(state.size)
  state.size += partSize(part)
}

// Bytes and blobs are stored as they are; only what has no byte form yet — a
// string, a bare ArrayBuffer — is given one, as a view where it can be. A
// nested BlobArray is taken apart rather than nested.
const merge = (state: BlobState, inputs: BlobInput): BlobState => {
  for (const v of ToArray(inputs)) {
    if (StringQ(v)) {
      add(state, StringToUInt8(v))
    } else if (v instanceof ArrayBuffer) {
      add(state, new Uint8Array(v))
    } else if ("offsets" in v) {
      for (const part of v.parts) add(state, part)
    } else {
      add(state, v)
    }
  }
  return state
}

const blankState = (): BlobState => ({ offsets: [], parts: [], size: 0 })

// Each part becomes bytes once — platform blobs read concurrently — and one
// merge with the known total lays them into a single fresh allocation.
const arrayBuffer = ({ parts, size }: BlobState): Promise<ArrayBuffer> =>
  WaitPromises(parts.map(partBytes)).then(chunks => UInt8Merge(chunks, size as Bytes).buffer)

const arrayBufferSync = ({ parts, size }: BlobState): ArrayBuffer =>
  UInt8Merge(parts.map(partBytesSync), size as Bytes).buffer

// The parts the range touches, trimmed at the two edges and shared in the
// middle: a slice of a hundred-part blob re-holds ninety-eight of them as they
// are and takes a view of two. Nothing is read.
const slice = (state: BlobState, type: string, start: number, end: number): BlobArrayType => {
  const { offsets, parts, size } = state
  if (start < 0) start = Max(0, size + start)
  if (end < 0) end = Max(0, size + end)
  end = Min(end, size)
  if (start >= end || start >= size) {
    return createBlobArray(blankState(), type)
  }

  const startIdx = findPartIndex(state, start)
  const endIdx = findPartIndex(state, end - 1)

  const result = blankState()
  for (let i = startIdx; i <= endIdx; i++) {
    const part = parts[i]
    const from = Max(0, start - offsets[i])
    const to = Min(partSize(part), end - offsets[i])
    add(result, from === 0 && to === partSize(part) ? part : partView(part, from, to))
  }

  return createBlobArray(result, type)
}

const createBlobArray = (state: BlobState, type = ""): BlobArrayType => {
  const result: BlobArrayType = {
    ...state,
    arrayBuffer: () => arrayBuffer(result),
    arrayBufferSync: () => arrayBufferSync(result),
    clear: () => clear(result),
    push: inputs => merge(result, inputs),
    slice: (start = 0, end = state.size) => slice(result, type, start, end),
    type,
  }
  return result
}

export const BlobArray: BlobCreateType<BlobArrayType> = (inputs = [], type = ContentType.BYTES) =>
  createBlobArray(merge(blankState(), inputs), type)
